import {DataStatus, Segment} from 'platform/components';
import {Show, Space, VStack} from 'platform/foundation';

import {forwardRef, useState} from 'react';

import {
  GetServiceCaseOrderVariantsApiResponse,
  GetServiceOrderApiResponse,
  GetServiceOrderIntegrationsApiResponse,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useOrderAdditionalInformationSections} from '../../../../../pages/ServiceCaseDetail/hooks/useOrderAdditionalInformationSections';
import {OrderDetailFormRef} from '../utils/orderDetailFormRef';
import {OrderCommercialForm} from './OrderCommercialForm';
import {OrderComplaintForm} from './OrderComplaintForm';
import {OrderContractForm} from './OrderContractForm';
import {OrderDetailAdditionalInformation} from './OrderDetailAdditionalInformation';
import {OrderInsuranceForm} from './OrderInsuranceForm';
import {OrderInternalForm} from './OrderInternalForm';
import {OrderWarrantyForm} from './OrderWarrantyForm';

const BASIC_INFORMATION = 'BASIC_INFORMATION';
const ADDITIONAL_INFORMATION = 'ADDITIONAL_INFORMATION';
type SegmentType = typeof BASIC_INFORMATION | typeof ADDITIONAL_INFORMATION;

type OrderFormComponent = React.ForwardRefExoticComponent<
  {
    serviceCaseId: string;
    serviceOrderId: string;
    isReadOnly?: boolean;
  } & TestIdProps &
    React.RefAttributes<OrderDetailFormRef>
>;

const ORDER_FORM_MAP: Record<string, OrderFormComponent> = {
  COM: OrderCommercialForm,
  INS: OrderInsuranceForm,
  INT: OrderInternalForm,
  CON: OrderContractForm,
  WAR: OrderWarrantyForm,
  CLA: OrderComplaintForm,
};

interface OrderDetailProps extends TestIdProps {
  orderId: string;
  serviceCaseId: string;
  serviceOrder: GetServiceOrderApiResponse;
  orderVariants: GetServiceCaseOrderVariantsApiResponse;
  orderIntegrations: GetServiceOrderIntegrationsApiResponse;
  isLoading: boolean;
  isError: boolean;
  orderType: string | undefined | null;
  isReadOnly?: boolean;
}

export const OrderDetail = forwardRef<OrderDetailFormRef, OrderDetailProps>((props, ref) => {
  const [selectedSegment, setSelectedSegment] = useState<SegmentType>(BASIC_INFORMATION);

  const {
    hasAnySection: hasAdditionalInformation,
    isLoading: isSectionsLoading,
    isError: isSectionsError,
  } = useOrderAdditionalInformationSections({
    serviceCaseId: props.serviceCaseId,
    orderId: props.orderId,
    orderIntegrations: props.orderIntegrations,
  });

  const isLoading = props.isLoading || isSectionsLoading;
  const isError = props.isError || isSectionsError;
  const currentSegment =
    selectedSegment === ADDITIONAL_INFORMATION && !hasAdditionalInformation
      ? BASIC_INFORMATION
      : selectedSegment;
  const FormComponent = props.orderType ? ORDER_FORM_MAP[props.orderType] : null;

  return (
    <DataStatus
      isLoading={isLoading}
      isError={isError}
      isEmpty={!props.serviceOrder}
      minHeight={144}
    >
      <Show when={hasAdditionalInformation}>
        <VStack align="flex-start">
          <Segment
            minWidth={42}
            value={currentSegment}
            onChange={setSelectedSegment}
            options={[
              {label: i18n.t('general.labels.basicInformation'), value: BASIC_INFORMATION},
              {
                label: i18n.t('general.labels.additionalInformation'),
                value: ADDITIONAL_INFORMATION,
              },
            ]}
          />
        </VStack>
        <Space vertical={4} />
      </Show>
      <Show when={currentSegment === ADDITIONAL_INFORMATION}>
        <OrderDetailAdditionalInformation
          orderId={props.orderId}
          serviceCaseId={props.serviceCaseId}
          data-testid={suffixTestId('additionalInformation', props)}
        />
      </Show>
      <Show when={currentSegment === BASIC_INFORMATION && FormComponent}>
        {(FormComponent) => (
          <FormComponent
            ref={ref}
            serviceOrderId={props.orderId}
            serviceCaseId={props.serviceCaseId}
            isReadOnly={props.isReadOnly}
            data-testid={suffixTestId('detailForm', props)}
          />
        )}
      </Show>
    </DataStatus>
  );
});
